import React, { useState, useEffect, useCallback } from 'react';
import { useTenant } from '../contexts/TenantContext';
import { useAuthFetch } from '../hooks/useAuthFetch';
import { useUsage } from '../hooks/useUsage';
import { useAuth } from '../contexts/AuthContext';

interface BillingStatus {
  plan: string;
  hasStripeCustomer: boolean;
  subscriptionId: string | null;
  planStatus: string | null;
  planCancellingAt: number | null;
  billingEnabled: boolean;
  founderSlotsRemaining: number;
}

// Limits arrive as nested objects (resumeUploads = { monthly, maxStored }, fileSize = { maxBytes }, etc.)
type LimitValue = number | Record<string, number | boolean> | undefined;

interface ApiPlan {
  key: string;
  displayName: string;
  priceMonthlyUsd: number;
  priceAnnualUsd: number;
  publicVisible: boolean;
  tierOrder: number;
  limits: Record<string, LimitValue>;
  features: Record<string, boolean>;
}

interface ApiPricing {
  plans: Record<string, ApiPlan>;
  annualDiscountPct: number;
}

const TIER_RANK: Record<string, number> = { free: 0, starter: 1, pro: 2, business: 3, enterprise: 4 };

const FEATURE_LABELS: Record<string, string> = {
  vectorSearch: 'Semantic / vector search',
  llmReranking: 'LLM-powered reranking',
  bulkUpload: 'Bulk upload',
  apiAccess: 'API access',
  hrAgent: 'HR Agent (chat)',
  burstAddons: 'Burst pack add-ons',
  archiveStorage: 'Archive storage',
  sso: 'SSO / SAML',
  dedicatedSupport: 'Dedicated support',
};

// ---- limit accessors --------------------------------------------------------

function asNumber(v: unknown): number | undefined {
  return typeof v === 'number' ? v : undefined;
}

function fmtCount(n: number | undefined): string {
  if (n === undefined) return '—';
  if (n === -1) return 'Unlimited';
  return n.toLocaleString();
}

function getMonthly(limit: LimitValue): number | undefined {
  if (typeof limit === 'number') return limit;
  if (limit && typeof limit === 'object') return asNumber((limit as Record<string, unknown>).monthly);
  return undefined;
}
function getStored(limit: LimitValue): number | undefined {
  if (limit && typeof limit === 'object') return asNumber((limit as Record<string, unknown>).maxStored);
  return undefined;
}
function getMaxBytes(limit: LimitValue): number | undefined {
  if (limit && typeof limit === 'object') return asNumber((limit as Record<string, unknown>).maxBytes);
  if (typeof limit === 'number') return limit;
  return undefined;
}

// Each agent session is modeled as ~5 Q&As (one Q&A = one user question to the
// AI recruiting agent with a roughly one-page response). Surfacing Q&As on the
// pricing card matches the customer's mental model better than "sessions" and
// is the unit we'll enforce against once hard ceilings ship.
const QAS_PER_SESSION = 5;

interface KeyMetrics {
  activeJobs: string;
  monthlyResumes: string;
  storedResumes: string;
  seats: string;
  agentQas: string;
}

function getKeyMetrics(plan: ApiPlan): KeyMetrics {
  const seats = asNumber(plan.limits.seats);
  const monthlyAgentSessions = getMonthly(plan.limits.agentSessions);
  const agentQas =
    monthlyAgentSessions === undefined
      ? '—'
      : monthlyAgentSessions === 0
      ? 'Not included'
      : fmtCount(monthlyAgentSessions * QAS_PER_SESSION);
  return {
    activeJobs: fmtCount(asNumber(plan.limits.activeJobs)),
    monthlyResumes: fmtCount(getMonthly(plan.limits.resumeUploads)),
    storedResumes: fmtCount(getStored(plan.limits.resumeUploads)),
    seats: seats === -1 ? 'Unlimited' : seats !== undefined ? seats.toLocaleString() : '—',
    agentQas,
  };
}

interface DetailLine {
  label: string;
  included: boolean;
}

function buildDetails(plan: ApiPlan): DetailLine[] {
  const lines: DetailLine[] = [];
  const monthlyAgent = getMonthly(plan.limits.agentSessions);
  const maxBytes = getMaxBytes(plan.limits.fileSize);

  if (monthlyAgent !== undefined && monthlyAgent !== 0)
    lines.push({
      label: `${fmtCount(monthlyAgent * QAS_PER_SESSION)} AI Agent Q&As / mo (${fmtCount(monthlyAgent)} sessions × ~${QAS_PER_SESSION} Q&As)`,
      included: true,
    });
  if (maxBytes !== undefined) lines.push({ label: `${Math.round(maxBytes / 1_048_576)} MB max file size`, included: true });

  Object.entries(FEATURE_LABELS).forEach(([key, label]) => {
    lines.push({ label, included: !!plan.features[key] });
  });

  return lines;
}

// ---- component --------------------------------------------------------------

export const BillingPage: React.FC = () => {
  const { org, loading: tenantLoading } = useTenant();
  const { user } = useAuth();
  const { usage } = useUsage();
  const authFetch = useAuthFetch();
  const [billing, setBilling] = useState<BillingStatus | null>(null);
  const [pricing, setPricing] = useState<ApiPricing | null>(null);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [loadingBilling, setLoadingBilling] = useState(true);
  const [upgrading, setUpgrading] = useState<string | null>(null);
  const [managing, setManaging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedTier, setExpandedTier] = useState<string | null>(null);
  const [salesOpen, setSalesOpen] = useState(false);
  const [salesSubmitting, setSalesSubmitting] = useState(false);
  const [salesSuccess, setSalesSuccess] = useState<string | null>(null);
  const [salesError, setSalesError] = useState<string | null>(null);
  const [salesForm, setSalesForm] = useState({
    companyName: '',
    companySize: '11-50',
    useCase: '',
    message: '',
    contactEmail: '',
    contactName: '',
  });

  // Pre-fill modal whenever it opens with the latest auth/org context.
  useEffect(() => {
    if (salesOpen) {
      setSalesForm((prev) => ({
        ...prev,
        contactEmail: prev.contactEmail || user?.email || '',
        contactName: prev.contactName || user?.displayName || '',
        companyName: prev.companyName || org?.name || '',
      }));
      setSalesError(null);
      setSalesSuccess(null);
    }
  }, [salesOpen, user?.email, user?.displayName, org?.name]);

  const fetchBilling = useCallback(async () => {
    try {
      const res = await authFetch('/api/billing/status');
      if (res.ok) setBilling(await res.json());
    } catch {
      // billing endpoint may not be available
    } finally {
      setLoadingBilling(false);
    }
  }, [authFetch]);

  const fetchPricing = useCallback(async () => {
    try {
      const res = await fetch('/api/pricing');
      if (res.ok) setPricing(await res.json());
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => {
    fetchBilling();
    fetchPricing();
  }, [fetchBilling, fetchPricing]);

  const handleUpgrade = async (tierKey: string) => {
    // Enterprise is sales-led — open the Contact Sales modal instead of Stripe Checkout.
    if (tierKey === 'enterprise') {
      setSalesOpen(true);
      return;
    }
    setUpgrading(tierKey);
    setError(null);
    try {
      const currentUrl = window.location.href.split('#')[0];
      const res = await authFetch('/api/billing/create-checkout', {
        method: 'POST',
        body: JSON.stringify({
          success_url: `${currentUrl}#/billing?success=true`,
          cancel_url: `${currentUrl}#/billing?cancelled=true`,
          tier: tierKey,
          billing: billingCycle,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        window.location.href = data.url;
      } else {
        const err = await res.json().catch(() => ({ detail: 'Failed to start checkout' }));
        setError(err.detail || 'Failed to start checkout');
      }
    } catch {
      setError('Failed to connect to billing service');
    } finally {
      setUpgrading(null);
    }
  };

  const handleManage = async () => {
    setManaging(true);
    setError(null);
    try {
      const currentUrl = window.location.href.split('#')[0];
      const res = await authFetch('/api/billing/create-portal', {
        method: 'POST',
        body: JSON.stringify({ return_url: `${currentUrl}#/billing` }),
      });
      if (res.ok) {
        const data = await res.json();
        window.location.href = data.url;
      } else {
        const err = await res.json().catch(() => ({ detail: 'Failed to open billing portal' }));
        setError(err.detail || 'Failed to open billing portal');
      }
    } catch {
      setError('Failed to connect to billing service');
    } finally {
      setManaging(false);
    }
  };

  const handleSalesSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSalesSubmitting(true);
    setSalesError(null);
    setSalesSuccess(null);
    try {
      const res = await authFetch('/api/sales/enterprise-inquiry', {
        method: 'POST',
        body: JSON.stringify(salesForm),
      });
      if (res.ok) {
        setSalesSuccess("Thanks — we'll get back within 1 business day.");
        setSalesForm((prev) => ({ ...prev, useCase: '', message: '' }));
        // Auto-close after a short delay so the user sees the confirmation.
        setTimeout(() => setSalesOpen(false), 2200);
      } else {
        const err = await res.json().catch(() => ({ detail: 'Failed to send inquiry' }));
        setSalesError(err.detail || 'Failed to send inquiry');
      }
    } catch {
      setSalesError('Failed to connect to server');
    } finally {
      setSalesSubmitting(false);
    }
  };

  const currentPlan = (billing?.plan || org?.plan || 'free').toLowerCase();
  const currentRank = TIER_RANK[currentPlan] ?? 0;

  const displayTiers = pricing
    ? Object.entries(pricing.plans)
        .map(([key, p]) => ({ ...p, key }))
        .filter((p) => p.publicVisible)
        .sort((a, b) => a.tierOrder - b.tierOrder)
    : [];

  const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
  const showSuccess = params.get('success') === 'true';
  const showCancelled = params.get('cancelled') === 'true';

  if (tenantLoading || loadingBilling) {
    return (
      <div className="upload-page">
        <main className="upload-main">
          <div className="upload-container">
            <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-secondary)' }}>
              Loading billing...
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="upload-page">
      <main className="upload-main">
        <div className="upload-container">
          <div className="upload-form" style={{ maxWidth: 1280 }}>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>
              Billing & Plans
            </h1>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: 16 }}>
              {org?.name || 'Your workspace'} &middot; Current plan:{' '}
              <strong style={{ color: currentPlan !== 'free' ? 'var(--accent-primary)' : 'var(--text-primary)' }}>
                {currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)}
              </strong>
            </p>

            <div style={{ display: 'inline-flex', background: 'var(--bg-surface)', borderRadius: 999, padding: 4, marginBottom: 24, border: '1px solid var(--border-color)' }}>
              {(['monthly', 'annual'] as const).map((cycle) => (
                <button
                  key={cycle}
                  onClick={() => setBillingCycle(cycle)}
                  style={{
                    padding: '6px 16px',
                    background: billingCycle === cycle ? 'var(--accent-primary)' : 'transparent',
                    color: billingCycle === cycle ? '#fff' : 'var(--text-secondary)',
                    border: 'none',
                    borderRadius: 999,
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  {cycle === 'monthly' ? 'Monthly' : `Annual (save ${pricing?.annualDiscountPct ?? 15}%)`}
                </button>
              ))}
            </div>

            {showSuccess && (
              <div style={{ background: 'rgba(52, 211, 153, 0.1)', border: '1px solid rgba(52, 211, 153, 0.3)', borderRadius: 8, padding: '12px 16px', marginBottom: 20, color: '#34d399', fontSize: '0.9rem' }}>
                Payment successful! Your plan has been upgraded.
              </div>
            )}
            {showCancelled && (
              <div style={{ background: 'rgba(251, 191, 36, 0.1)', border: '1px solid rgba(251, 191, 36, 0.3)', borderRadius: 8, padding: '12px 16px', marginBottom: 20, color: '#fbbf24', fontSize: '0.9rem' }}>
                Checkout was cancelled. No charges were made.
              </div>
            )}
            {billing?.planStatus === 'payment_failed' && (
              <div style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: 8, padding: '12px 16px', marginBottom: 20, color: '#ef4444', fontSize: '0.9rem' }}>
                Your last payment failed. Please update your payment method to keep your access.
                <button onClick={handleManage} style={{ marginLeft: 12, textDecoration: 'underline', background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}>
                  Update payment method
                </button>
              </div>
            )}
            {billing?.planCancellingAt && (
              <div style={{ background: 'rgba(251, 191, 36, 0.1)', border: '1px solid rgba(251, 191, 36, 0.3)', borderRadius: 8, padding: '12px 16px', marginBottom: 20, color: '#fbbf24', fontSize: '0.9rem' }}>
                Your plan is set to cancel at the end of the current billing period. You'll retain access until then.
              </div>
            )}
            {error && (
              <div style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: 8, padding: '12px 16px', marginBottom: 20, color: '#ef4444', fontSize: '0.9rem' }}>
                {error}
              </div>
            )}

            {/* 5-tier compact row */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${displayTiers.length || 5}, minmax(0, 1fr))`,
                gap: 12,
                marginBottom: 24,
              }}
            >
              {displayTiers.map((plan) => {
                const tierKey = plan.key;
                const tierRank = TIER_RANK[tierKey] ?? 0;
                const isCurrentPlan = tierKey === currentPlan;
                const canUpgrade = tierRank > currentRank;
                const isPopular = tierKey === 'pro';
                const isEnterprise = tierKey === 'enterprise';
                const price = billingCycle === 'annual' ? plan.priceAnnualUsd : plan.priceMonthlyUsd;
                const priceLabel = isEnterprise ? 'Custom' : price === 0 ? '$0' : `$${price.toLocaleString()}`;
                const periodLabel = isEnterprise || price === 0 ? '' : billingCycle === 'annual' ? '/yr' : '/mo';
                const metrics = getKeyMetrics(plan);
                const isExpanded = expandedTier === tierKey;
                const showFounder = tierKey === 'pro' && (billing?.founderSlotsRemaining ?? 0) > 0 && !isCurrentPlan;

                const borderColor = isCurrentPlan
                  ? 'var(--accent-primary)'
                  : isPopular
                  ? 'rgba(99, 102, 241, 0.5)'
                  : 'var(--border-color)';

                return (
                  <div
                    key={tierKey}
                    style={{
                      background: 'var(--bg-surface)',
                      border: `${isCurrentPlan || isPopular ? '2px' : '1px'} solid ${borderColor}`,
                      borderRadius: 12,
                      padding: 16,
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      minWidth: 0,
                    }}
                  >
                    {(isCurrentPlan || isPopular) && (
                      <div style={{
                        position: 'absolute',
                        top: -10,
                        right: 12,
                        background: isCurrentPlan ? 'var(--accent-primary)' : '#6366f1',
                        color: '#fff',
                        padding: '2px 10px',
                        borderRadius: 10,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        letterSpacing: 0.3,
                      }}>
                        {isCurrentPlan ? 'CURRENT' : 'POPULAR'}
                      </div>
                    )}

                    <h3 style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
                      {plan.displayName}
                    </h3>
                    <div style={{ marginTop: 4, marginBottom: 12 }}>
                      <span style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-primary)' }}>{priceLabel}</span>
                      {periodLabel && <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}> {periodLabel}</span>}
                    </div>

                    {showFounder && (
                      <div style={{ background: 'rgba(52, 211, 153, 0.1)', border: '1px solid rgba(52, 211, 153, 0.3)', borderRadius: 6, padding: '4px 8px', marginBottom: 10, fontSize: '0.7rem', color: '#34d399', fontWeight: 600 }}>
                        Founder · {billing?.founderSlotsRemaining} left
                      </div>
                    )}

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: '0.8rem', color: 'var(--text-secondary)', flexGrow: 1 }}>
                      <KpiRow label="Active jobs" value={metrics.activeJobs} />
                      <KpiRow label="Resumes / mo" value={metrics.monthlyResumes} />
                      <KpiRow label="Stored resumes" value={metrics.storedResumes} />
                      <KpiRow label="Seats" value={metrics.seats} />
                      <KpiRow
                        label="🤖 AI Agent Q&As / mo"
                        value={metrics.agentQas}
                        title="Each Q&A is one question to your AI recruiting agent (~1 page response). Quotas reset monthly. No overage billing — upgrade for more."
                      />
                    </div>

                    <button
                      type="button"
                      onClick={() => setExpandedTier(isExpanded ? null : tierKey)}
                      style={{
                        marginTop: 10,
                        background: 'transparent',
                        border: 'none',
                        color: 'var(--accent-primary)',
                        fontSize: '0.75rem',
                        cursor: 'pointer',
                        padding: 0,
                        textAlign: 'left',
                      }}
                    >
                      {isExpanded ? 'Hide details ▴' : 'See details ▾'}
                    </button>

                    {isExpanded && (
                      <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0 0 0', borderTop: '1px solid var(--border-color)', paddingTop: 8 }}>
                        {buildDetails(plan).map((line, idx) => (
                          <li
                            key={idx}
                            style={{
                              padding: '2px 0',
                              fontSize: '0.75rem',
                              color: 'var(--text-secondary)',
                              opacity: line.included ? 1 : 0.45,
                            }}
                          >
                            <span style={{ color: line.included ? '#34d399' : 'inherit', marginRight: 6 }}>
                              {line.included ? '✓' : '✗'}
                            </span>
                            {line.label}
                          </li>
                        ))}
                      </ul>
                    )}

                    <div style={{ marginTop: 12 }}>
                      {(canUpgrade || (isEnterprise && !isCurrentPlan)) && (
                        <button
                          onClick={() => handleUpgrade(tierKey)}
                          disabled={!isEnterprise && (!!upgrading || !billing?.billingEnabled)}
                          style={{
                            width: '100%',
                            padding: '8px 0',
                            background: isPopular ? 'var(--accent-primary)' : 'transparent',
                            color: isPopular ? '#fff' : 'var(--accent-primary)',
                            border: `1px solid var(--accent-primary)`,
                            borderRadius: 8,
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            cursor: upgrading ? 'not-allowed' : 'pointer',
                            opacity: upgrading && !isEnterprise ? 0.6 : 1,
                          }}
                        >
                          {upgrading === tierKey ? 'Redirecting…' : isEnterprise ? 'Contact sales' : 'Upgrade'}
                        </button>
                      )}
                      {isCurrentPlan && currentPlan !== 'free' && (
                        <button
                          onClick={handleManage}
                          disabled={managing}
                          style={{
                            width: '100%',
                            padding: '8px 0',
                            background: 'transparent',
                            color: 'var(--text-secondary)',
                            border: '1px solid var(--border-color)',
                            borderRadius: 8,
                            fontSize: '0.8rem',
                            cursor: managing ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {managing ? 'Opening…' : 'Manage'}
                        </button>
                      )}
                      {isCurrentPlan && currentPlan === 'free' && (
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', textAlign: 'center', padding: '8px 0' }}>
                          You're here
                        </div>
                      )}
                      {!canUpgrade && !isCurrentPlan && (
                        <button
                          onClick={handleManage}
                          disabled={managing}
                          style={{
                            width: '100%',
                            padding: '8px 0',
                            background: 'transparent',
                            color: 'var(--text-secondary)',
                            border: '1px solid var(--border-color)',
                            borderRadius: 8,
                            fontSize: '0.75rem',
                            cursor: managing ? 'not-allowed' : 'pointer',
                          }}
                        >
                          Downgrade
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Current usage summary */}
            {usage && (
              <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-color)', borderRadius: 12, padding: 20 }}>
                <h3 style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 12 }}>
                  Current Period Usage
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12 }}>
                  {(() => {
                    const fmt = (used: number, limit: number | undefined): string => {
                      if (limit === undefined || limit === null) return `${used}`;
                      if (limit === -1) return `${used} / Unlimited`;
                      if (limit === 0) return 'Not included';
                      return `${used} / ${limit}`;
                    };
                    const ru = usage.usage?.resumeUploads;
                    const ag = usage.usage?.agentSessions;
                    const aj = usage.usage?.activeJobs;
                    const sk = usage.usage?.seats;
                    const QAS = 5; // Q&As per session, matches pricing card unit
                    const stats: Array<{ label: string; value: string; sub?: string }> = [
                      {
                        label: '📤 Resume Uploads',
                        value: fmt(ru?.used ?? 0, ru?.monthly),
                        sub: 'this month',
                      },
                      {
                        label: '💾 Stored Resumes',
                        value: fmt(ru?.stored ?? 0, ru?.maxStored),
                        sub: 'in library',
                      },
                      {
                        label: '💼 Active Jobs',
                        value: fmt(aj?.current ?? 0, aj?.limit),
                        sub: 'currently open',
                      },
                      {
                        label: '🤖 AI Agent Q&As',
                        value:
                          ag?.monthly === 0
                            ? 'Not included'
                            : ag?.monthly === -1
                              ? `${(ag?.used ?? 0) * QAS} / Unlimited`
                              : `${(ag?.used ?? 0) * QAS} / ${(ag?.monthly ?? 0) * QAS}`,
                        sub:
                          ag?.dailyCap && ag.dailyCap > 0
                            ? `${(ag.dailyUsed ?? 0) * QAS} / ${ag.dailyCap * QAS} today`
                            : 'this month',
                      },
                      {
                        label: '👥 Seats',
                        value: fmt(sk?.current ?? 0, sk?.limit),
                        sub: 'members + invites',
                      },
                    ];
                    return stats.map((stat) => (
                      <div key={stat.label}>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: 4 }}>{stat.label}</div>
                        <div style={{ fontSize: '1.1rem', fontWeight: 600, color: 'var(--text-primary)' }}>{stat.value}</div>
                        {stat.sub && (
                          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>{stat.sub}</div>
                        )}
                      </div>
                    ));
                  })()}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
      {salesOpen && (
        <ContactSalesModal
          form={salesForm}
          setForm={setSalesForm}
          onClose={() => setSalesOpen(false)}
          onSubmit={handleSalesSubmit}
          submitting={salesSubmitting}
          successMsg={salesSuccess}
          errorMsg={salesError}
        />
      )}
    </div>
  );
};

interface SalesFormState {
  companyName: string;
  companySize: string;
  useCase: string;
  message: string;
  contactEmail: string;
  contactName: string;
}

const ContactSalesModal: React.FC<{
  form: SalesFormState;
  setForm: React.Dispatch<React.SetStateAction<SalesFormState>>;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  submitting: boolean;
  successMsg: string | null;
  errorMsg: string | null;
}> = ({ form, setForm, onClose, onSubmit, submitting, successMsg, errorMsg }) => {
  const update = (k: keyof SalesFormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm((prev) => ({ ...prev, [k]: e.target.value }));

  // Light palette to match the Add Candidates modal in JobWorkspacePage —
  // hardcoded so the modal stays legible regardless of CSS-var resolution.
  const C = {
    surface: '#ffffff',
    inputBg: '#ffffff',
    inputText: '#111111',
    placeholder: '#9ca3af',
    border: '#d1d5db',
    borderFocus: '#3b82f6',
    label: '#374151',
    heading: '#111111',
    body: '#374151',
    muted: '#6b7280',
    accent: '#3b82f6',
    accentText: '#ffffff',
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '9px 11px',
    background: C.inputBg,
    color: C.inputText,
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    fontSize: '0.875rem',
    boxSizing: 'border-box',
    outline: 'none',
  };
  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '0.75rem',
    color: C.label,
    marginBottom: 4,
    marginTop: 12,
    fontWeight: 500,
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.65)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
    >
      {/* Scoped overrides for placeholder + select option contrast */}
      <style>{`
        .sales-modal input::placeholder,
        .sales-modal textarea::placeholder { color: ${C.placeholder}; opacity: 1; }
        .sales-modal input:focus,
        .sales-modal textarea:focus,
        .sales-modal select:focus { border-color: ${C.borderFocus} !important; box-shadow: 0 0 0 3px rgba(59,130,246,0.15); }
        .sales-modal select option { background: ${C.inputBg}; color: ${C.inputText}; }
      `}</style>
      <div
        className="sales-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.surface,
          border: `1px solid ${C.border}`,
          borderRadius: 12,
          padding: 24,
          width: '100%',
          maxWidth: 520,
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
          <h2 style={{ fontSize: '1.15rem', fontWeight: 600, color: C.heading, margin: 0 }}>
            Talk to sales — Enterprise
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{ background: 'none', border: 'none', color: C.label, fontSize: '1.4rem', lineHeight: 1, cursor: 'pointer', padding: 0 }}
          >
            ×
          </button>
        </div>
        <p style={{ fontSize: '0.825rem', color: C.body, marginTop: 4, marginBottom: 8, lineHeight: 1.45 }}>
          Tell us a little about your team and we'll get back within 1 business day with custom pricing,
          SSO, archive storage, and burst pack options.
        </p>

        {successMsg && (
          <div style={{ background: 'rgba(52, 211, 153, 0.1)', border: '1px solid rgba(52, 211, 153, 0.3)', borderRadius: 8, padding: '10px 12px', marginTop: 12, color: '#34d399', fontSize: '0.85rem' }}>
            {successMsg}
          </div>
        )}
        {errorMsg && (
          <div style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: 8, padding: '10px 12px', marginTop: 12, color: '#ef4444', fontSize: '0.85rem' }}>
            {errorMsg}
          </div>
        )}

        <form onSubmit={onSubmit}>
          <label style={labelStyle}>Your name</label>
          <input style={inputStyle} value={form.contactName} onChange={update('contactName')} required maxLength={200} />

          <label style={labelStyle}>Work email</label>
          <input
            style={inputStyle}
            type="email"
            value={form.contactEmail}
            onChange={update('contactEmail')}
            required
            maxLength={200}
          />

          <label style={labelStyle}>Company name</label>
          <input style={inputStyle} value={form.companyName} onChange={update('companyName')} required maxLength={200} />

          <label style={labelStyle}>Company size</label>
          <select style={inputStyle} value={form.companySize} onChange={update('companySize')} required>
            <option value="1-10">1–10</option>
            <option value="11-50">11–50</option>
            <option value="51-200">51–200</option>
            <option value="201-500">201–500</option>
            <option value="501-1000">501–1,000</option>
            <option value="1000+">1,000+</option>
          </select>

          <label style={labelStyle}>What are you trying to solve? (optional)</label>
          <input
            style={inputStyle}
            value={form.useCase}
            onChange={update('useCase')}
            maxLength={400}
            placeholder="e.g., Replace ATS keyword search with semantic ranking"
          />

          <label style={labelStyle}>Anything else? (optional)</label>
          <textarea
            style={{ ...inputStyle, minHeight: 90, fontFamily: 'inherit', resize: 'vertical' }}
            value={form.message}
            onChange={update('message')}
            maxLength={4000}
            placeholder="Volume, integrations, timeline…"
          />

          <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              style={{
                padding: '9px 16px',
                background: 'transparent',
                color: C.body,
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                fontSize: '0.85rem',
                fontWeight: 500,
                cursor: submitting ? 'not-allowed' : 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !!successMsg}
              style={{
                padding: '9px 18px',
                background: C.accent,
                color: C.accentText,
                border: 'none',
                borderRadius: 8,
                fontSize: '0.85rem',
                fontWeight: 600,
                cursor: submitting || !!successMsg ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.7 : 1,
              }}
            >
              {submitting ? 'Sending…' : successMsg ? 'Sent ✓' : 'Send inquiry'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const KpiRow: React.FC<{ label: string; value: string; title?: string }> = ({ label, value, title }) => (
  <div
    title={title}
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      gap: 8,
      cursor: title ? 'help' : 'default',
    }}
  >
    <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', opacity: 0.85 }}>{label}</span>
    <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)' }}>{value}</span>
  </div>
);
